'use client';

import { Trash2 } from 'lucide-react';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { VerticalReorderingList } from '@/components/vertical-reordering-list';

import OtherOptionField from './other-option-field';
import { AddOptionButton } from './shared/add-option-button';
import { OptionRow } from './shared/option-row';
import { useFieldOptions } from './shared/use-field-options';

interface ImperativeHandleProps {
	focusOnFirstOption: () => void;
}
interface IMultiSelectFieldProps {
	options: string[];
	selectedOptions?: string[];
	onSelectOptions?: (updatedOptions: string[]) => void;
	editMode?: boolean;
	isFocusedInput?: boolean;
	onChangeOptions?: (updatedOptions: string[]) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	disabled?: boolean;
	allowOther?: boolean;
	onAllowOtherChange?: (value: boolean) => void;
}

const MultiSelectField = forwardRef<ImperativeHandleProps, IMultiSelectFieldProps>(
	(
		{
			options,
			selectedOptions = [],
			onSelectOptions,
			editMode,
			onChangeOptions,
			onFocus,
			onBlur,
			isFocusedInput,
			disabled,
			allowOther = false,
			onAllowOtherChange,
		},
		ref
	) => {
		const [otherSelected, setOtherSelected] = useState(false);
		const [parsedSelectedOptions, setParsedSelectedOptions] = useState<{
			other: string;
			selected: string[];
		}>({
			other: '',
			selected: [],
		});

		const {
			optionsCopy,
			displayDeleteButton,
			displayReorderIcon,
			displayAddOption,
			focusOnFirstOption,
			registerOptionRef,
			handleOnReorderOptions,
			handleOnDeleteOption,
			onClickAddOption,
			handleInputOptionChange,
			handleInputOptionFocus,
			handleInputOptionBlur,
			handleInputOptionKeyDown,
		} = useFieldOptions({ options, onChangeOptions, editMode, isFocusedInput, onFocus, onBlur });

		useImperativeHandle(ref, () => ({ focusOnFirstOption }), [focusOnFirstOption]);

		const handleOnSelectOption = (index: number, isSelected: boolean) => () => {
			if (!isSelected) {
				onSelectOptions?.(
					selectedOptions.filter((selectedOption) => selectedOption !== options[index])
				);
			} else {
				onSelectOptions?.([...selectedOptions, options[index] || '']);
			}
		};

		const handleOtherSelect = useCallback(
			(selected: boolean) => {
				onSelectOptions?.(parsedSelectedOptions.selected);
				setOtherSelected(selected);
			},
			[onSelectOptions, parsedSelectedOptions]
		);

		const handleOtherOptionChange = useCallback(
			(text: string) => {
				if (text) {
					onSelectOptions?.([...parsedSelectedOptions.selected, text]);
				} else {
					onSelectOptions?.(parsedSelectedOptions.selected);
				}
			},
			[onSelectOptions, parsedSelectedOptions]
		);

		useEffect(() => {
			if (editMode) return;

			const tempOptions = [...options];
			const selected = [];
			let other = '';
			for (const option of selectedOptions) {
				if (tempOptions.includes(option)) {
					selected.push(option);
					tempOptions.splice(tempOptions.indexOf(option), 1);
				} else {
					other = option;
				}
			}
			setParsedSelectedOptions({ other, selected });
		}, [selectedOptions]);

		return (
			<div className="flex w-full max-w-full flex-col">
				<VerticalReorderingList
					items={optionsCopy}
					onReorder={handleOnReorderOptions}
					className="w-full"
					getItemId={(option) => option.id}
					renderItem={(option, index) => {
						const isSelected = selectedOptions.includes(option.name);

						return (
							<OptionRow
								key={option.id}
								option={option}
								index={index}
								editMode={editMode}
								itemClassName="px-0"
								leadingControl={
									<Checkbox
										id={option.id}
										checked={isSelected}
										onCheckedChange={handleOnSelectOption(index, !isSelected)}
										disabled={editMode || disabled}
										className="my-[2px] h-5 w-5 self-start border-2 border-slate-700 disabled:opacity-100 data-[state=checked]:border-primary"
									/>
								}
								showNonEditLabel
								displayDeleteButton={displayDeleteButton}
								displayReorderIcon={displayReorderIcon}
								registerRef={registerOptionRef(option.id)}
								onChangeValue={handleInputOptionChange(option.id)}
								onFocus={handleInputOptionFocus}
								onBlur={handleInputOptionBlur}
								onKeyDown={handleInputOptionKeyDown(option.id)}
								onDelete={handleOnDeleteOption(option.id)}
							/>
						);
					}}
				/>
				{displayAddOption && (
					<AddOptionButton
						onAddOption={onClickAddOption()}
						onAddOther={() => onAllowOtherChange?.(true)}
						hideOther={allowOther}
						showCheckbox
					/>
				)}

				{allowOther && (
					<div className="group flex items-start justify-between gap-2">
						<OtherOptionField
							showCheckbox
							disabled={editMode}
							selected={otherSelected && !editMode}
							onSelect={handleOtherSelect}
							onInputChange={(value) => (editMode ? null : handleOtherOptionChange(value))}
							inputValue={parsedSelectedOptions.other}
						/>

						{displayDeleteButton && (
							<Button
								variant={'ghost'}
								className="opacity-hover:bg-transparent pointer-events-none h-5 w-5 p-0 text-slate-400 opacity-0 group-hover:pointer-events-auto group-hover:opacity-100"
								onClick={() => onAllowOtherChange?.(false)}
							>
								<Trash2 className="h-5 w-5" />
							</Button>
						)}
					</div>
				)}
			</div>
		);
	}
);

MultiSelectField.displayName = 'MultiSelectField';

export default MultiSelectField;
